import React, { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import thankYouImg from "../../../../assets/images/nh_consumer_success.svg";
import completionTick from "../../../../assets/images/completion.svg";
import Footer from "../../../commonComponents/footer/Footer.js";
import { useNavigate } from "react-router-dom";
import Header from "../../../commonComponents/header/Header.js";
import { apiService } from "../../../../services/apiService.js";

const EndInterviewOverlay = ({
  onComplete,
  onRedirectToLogin,
  // True while the bot still has audio queued/playing (isBotSpeaking,
  // isBotBusy, or queuedBotMessages.length > 0 upstream). We must not
  // redirect away while this is true, otherwise the last bot message can
  // get cut off mid-playback.
  isBotAudioPending = false,
}) => {
  const navigate = useNavigate();
  const [isProcessingComplete, setIsProcessingComplete] = useState(false);
  const hasRedirectedRef = useRef(false);

  // Function to stop all media recordings
  const stopAllRecordings = async () => {
    try {
      if (window.mediaRecorder && window.mediaRecorder.state !== "inactive") {
        window.mediaRecorder.stop();
      }

      if (window.currentMediaStream) {
        window.currentMediaStream.getTracks().forEach((track) => {
          track.stop();
        });
      }

      const stream = await navigator.mediaDevices
        .getUserMedia({
          video: false,
          audio: false,
        })
        .catch(() => null);

      navigator.mediaDevices.enumerateDevices().then((devices) => {
        if (navigator.mediaSession) {
          navigator.mediaSession.playbackState = "none";
        }
      });

      if (window.screenRecorder) {
        window.screenRecorder.stop();
      }

      if (navigator.mediaDevices && navigator.mediaDevices.getUserMedia) {
        try {
          const devices = await navigator.mediaDevices.enumerateDevices();
        } catch (error) {}
      }

      window.dispatchEvent(new CustomEvent("stopRecording"));
    } catch (error) {
      console.error("Error stopping recordings:", error);
    }
  };

  // NOTE: marking the interview complete (Firestore status 999) used to
  // happen here too, on mount. That's now done as soon as FBK is received,
  // in MeetingContainer's fbk-received handler — this overlay can render
  // well after that point, so waiting for mount was delaying it. Recordings
  // still stop here on mount, alongside the overlay's own UI pacing (how
  // long the spinner shows before we consider things "done").
  useEffect(() => {
    stopAllRecordings();

    const timer = setTimeout(() => {
      setIsProcessingComplete(true);
    }, 6000);

    return () => clearTimeout(timer);
  }, []);
  const clearAllUnloadGuards = () => {
    window.onbeforeunload = null;

    // Extra safety: remove any attached listeners
    window.removeEventListener("beforeunload", () => {});
  };

  // Updated: Wait for peripheral record API to finish before redirect
  useEffect(() => {
    const finalizeAndRedirect = async () => {
      clearAllUnloadGuards();

      const userLogsRaw = sessionStorage.getItem("userInteractionLogs");
      const interviewId = sessionStorage.getItem("interviewId");

      try {
        if (interviewId && userLogsRaw) {
          await apiService.setInterviewPeripheralRecord(
            interviewId,
            JSON.parse(userLogsRaw),
          );
        }
      } catch (e) {
        console.warn("Peripheral API failed, continuing anyway");
      }
      // redirect responsibility moved to safeRedirectToSuccess — don't duplicate it here
    };

    if (!isProcessingComplete) return;
    if (isBotAudioPending) return;
    if (hasRedirectedRef.current) return;
    hasRedirectedRef.current = true;

    finalizeAndRedirect().then(() => safeRedirectToSuccess());
  }, [isProcessingComplete, isBotAudioPending, navigate]);
  const safeRedirectToSuccess = async () => {
    //  ALLOW SAFE EXIT
    window.__ALLOW_SUCCESS_REDIRECT__ = true;

    // HARD remove unload handlers
    window.onbeforeunload = null;
    window.removeEventListener("beforeunload", () => {});

    //  Stop all media cleanly
    try {
      if (window.mediaRecorder?.state !== "inactive") {
        window.mediaRecorder.stop();
      }

      if (window.currentMediaStream) {
        window.currentMediaStream.getTracks().forEach((t) => t.stop());
        window.currentMediaStream = null;
      }

      if (window.audioContext) {
        await window.audioContext.close();
        window.audioContext = null;
      }

      if (window.VideoSDK?.destroy) {
        await window.VideoSDK.destroy();
      }
    } catch {}

    // Give browser time to settle (CRITICAL)
    await new Promise((res) => requestAnimationFrame(res));
    await new Promise((res) => setTimeout(res, 0));

    // Redirect WITHOUT POPUP
    window.location.replace("/assessment-success");
  };

  return (
    <div className="fixed inset-0 bg-white flex flex-col z-[9999] assement-font-color p-[16px]">
      <Header />
      {/* Main Content */}
      <div className="flex-1 flex">
        {/* Left Side - Illustration */}
        <div className="flex-1 flex items-center justify-center pl-[34px] pr-[50px]">
          <img
            src={thankYouImg}
            alt="Thank you illustration"
            className=" w-[100%] h-auto rounded-3xl"
          />
        </div>

        {/* Divider */}
        <div className="hidden md:block border-l border-dividerColor my-[5%]" />

        {/* Right Side - Content */}
        <div className="flex-1 flex items-center justify-center px-1">
          <div className="max-w-3xl w-full text-center">
            <h1 className="text-3xl font-medium text-gray-900 mb-6 assement-font-color assessment-first-font">
              Assessment Complete!
            </h1>

            <>
              <p className="mb-2 assessment-second-font">
                Thank you for your time.
              </p>
              <p className="mb-6 assessment-second-font">
                If shortlisted, someone from our team will reach out to you
                soon.
              </p>
              <p className="mb-6 assessment-second-font">
                Please wait while we process and finalise your assessment
                session...
              </p>

              {/* Loader Box with Old Spinner */}
              <div className="border assessment-progress-bg rounded-lg px-6 py-6 inline-flex flex-col items-center mt-4">
                <div className="flex items-center justify-center mb-4">
                  <div className="relative w-12 h-12 animate-spin-slow">
                    {[...Array(12)].map((_, i) => (
                      <span
                        key={i}
                        className="absolute left-1/2 top-1/2 block w-1 h-2.5 rounded animate-spinner-bar"
                        style={{
                          backgroundColor: "#111729",
                          transform: `rotate(${i * 30}deg) translate(0, -150%)`,
                          animationDelay: `${i * 0.1}s`,
                        }}
                      />
                    ))}
                  </div>
                </div>
                <p className="assessment-second-font text-sm">
                  {isBotAudioPending
                    ? "Finishing the last message..."
                    : "Saving responses and closing your assessment..."}
                </p>
              </div>
            </>
          </div>
        </div>
      </div>

      {/* Footer pinned at bottom */}
      <div>
        <Footer />
      </div>
    </div>
  );
};

export default EndInterviewOverlay;
